import { ArrowLeft, CopySimple, CalendarBlank, CurrencyEur, User, MapPin, Phone, EnvelopeSimple, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

function formatOfferDate(iso, language) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const locale = language === 'en' ? 'en-GB' : 'nl-NL';
  return d.toLocaleString(locale, {
    dateStyle: 'long',
    timeStyle: 'short',
  });
}

const Field = ({ label, value }) => (
  <div className="rounded-xl border border-[#e2e8f0] bg-white px-4 py-3">
    <p className="text-xs uppercase tracking-wide text-[#94a3b8] mb-1">{label}</p>
    <p className="text-sm font-medium text-[#0f172a] break-words">{value || '—'}</p>
  </div>
);

const OfferDetailView = ({ offer, onBack, onCreateOrder = null }) => {
  const { t, language } = useLanguage();
  const copyFullId = async () => {
    const full = String(offer?.offer_number || '').trim();
    if (!full) return;
    try {
      await navigator.clipboard.writeText(full);
      toast.success(t('offerCopied'));
    } catch {
      toast.error(t('offerCopyFailed'));
    }
  };

  return (
    <div className="animate-fadeIn space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="vmk-btn inline-flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            {t('backToOffers')}
          </button>
          {typeof onCreateOrder === 'function' && (
            <button
              type="button"
              onClick={() => onCreateOrder(offer)}
              className="vmk-btn vmk-btn-primary inline-flex items-center gap-2"
              data-testid="offer-detail-create-order"
            >
              <Plus size={16} weight="bold" />
              {t('createOrder')}
            </button>
          )}
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wide text-[#94a3b8]">{t('offerIdLabel')}</p>
          <div className="flex items-center justify-end gap-2">
            <p className="font-mono text-base font-semibold text-[#00A88C]">{offer.offer_number}</p>
            <button
              type="button"
              onClick={copyFullId}
              title={`Copy full ID: ${offer.offer_number}`}
              className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#e2e8f0] text-[#64748b] hover:text-[#00CBA9] hover:border-[#00CBA9] transition-colors"
            >
              <CopySimple size={15} />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-[#e2e8f0] bg-white p-5">
          <p className="text-xs uppercase tracking-wide text-[#94a3b8] mb-2">{t('amount')}</p>
          <div className="flex items-center gap-2 text-[#0f172a]">
            <CurrencyEur size={18} />
            <p className="text-2xl font-bold">
              {Number(offer.total_amount || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>
        <div className="rounded-2xl border border-[#e2e8f0] bg-white p-5">
          <p className="text-xs uppercase tracking-wide text-[#94a3b8] mb-2">{t('status')}</p>
          <span className="status-badge accepted">{offer.status || 'accepted'}</span>
        </div>
        <div className="rounded-2xl border border-[#e2e8f0] bg-white p-5">
          <p className="text-xs uppercase tracking-wide text-[#94a3b8] mb-2">{t('offerCreated')}</p>
          <div className="flex items-center gap-2 text-[#0f172a]">
            <CalendarBlank size={18} />
            <p className="font-semibold">{formatOfferDate(offer.created_at, language)}</p>
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-[#e2e8f0] bg-white p-5">
        <p className="text-xs uppercase tracking-wide text-[#94a3b8] mb-2">{t('offerAccepted')}</p>
        <div className="flex items-center gap-2 text-[#0f172a]">
          <CalendarBlank size={18} />
          <p className="font-semibold">{formatOfferDate(offer.accepted_at, language)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <section className="rounded-2xl border border-[#e2e8f0] bg-white p-5 space-y-4">
          <h3 className="text-lg font-semibold text-[#0f172a]">{t('offerClientDetails')}</h3>
          <div className="space-y-3">
            <Field
              label={t('klant')}
              value={
                <span className="inline-flex items-center gap-2">
                  <User size={16} className="text-[#94a3b8]" />
                  {offer?.contact?.name}
                </span>
              }
            />
            <Field
              label={t('address')}
              value={
                <span className="inline-flex items-center gap-2">
                  <MapPin size={16} className="text-[#94a3b8]" />
                  {[offer?.contact?.address, offer?.contact?.postcode].filter(Boolean).join(', ')}
                </span>
              }
            />
          </div>
        </section>

        <section className="rounded-2xl border border-[#e2e8f0] bg-white p-5 space-y-4">
          <h3 className="text-lg font-semibold text-[#0f172a]">{t('offerContactDetails')}</h3>
          <div className="space-y-3">
            <Field
              label={t('telefoonnummer')}
              value={
                <span className="inline-flex items-center gap-2">
                  <Phone size={16} className="text-[#94a3b8]" />
                  {offer?.contact?.phone || '—'}
                </span>
              }
            />
            <Field
              label={t('emailLabel')}
              value={
                <span className="inline-flex items-center gap-2">
                  <EnvelopeSimple size={16} className="text-[#94a3b8]" />
                  {offer?.contact?.email || '—'}
                </span>
              }
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default OfferDetailView;
